/**
 * Local multi-select with tags and optional async search dropdown.
 * Used only by calling-rule, POC-assignment, and POC-reassignment modal action fields.
 * Query builder condition rows use @risalabs_frontend_org/oasis-ui-kit MultiSelectWithTag.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, TextInput } from "risa-oasis-ui_v2";
import ChevronDown from "../../svg/chevron-down";
import { CrossIcon } from "../../svg/cross-icon";

/** Option shape for dropdown rows and tag labels. */
export interface MultiSelectWithTagOption {
  id: string;
  label: string;
}

export interface MultiSelectWithTagProps {
  options: MultiSelectWithTagOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  className?: string;
  /** Minimum width of the dropdown panel (e.g. `"5rem"`). */
  dropdownWidth?: string;
  onSearch?: (query: string) => Promise<MultiSelectWithTagOption[]>;
  onOpenChange?: (open: boolean) => void;
  searchPlaceholder?: string;
  searchMinChars?: number;
  searchDebounceMs?: number;
  searchNoResultsMessage?: string;
  /** When true, lets the user add the current search text as a tag if search returns nothing. */
  allowCustomValueOnEmptySearch?: boolean;
  onFooterApply?: (selectedIds: string[]) => void;
  footerPrimaryLabel?: string;
  hideFooter?: boolean;
  /** When `"single"`, only one id may be selected at a time. */
  selectionMode?: "single" | "multiple";
}

/**
 * Resolves display labels for selected ids from options and fallbacks.
 *
 * @param options - Current dropdown options
 * @param value - Selected option ids
 * @returns Options with labels for tag rendering
 */
function resolveSelectedOptions(
  options: MultiSelectWithTagOption[],
  value: string[],
): MultiSelectWithTagOption[] {
  const byId = new Map(options.map((o) => [o.id, o]));
  return value.map((id) => byId.get(id) ?? { id, label: id });
}

/**
 * Multi-select control with removable tags, searchable dropdown, and optional footer.
 */
const MultiSelectWithTag = ({
  options,
  value,
  onChange,
  placeholder = "Select value...",
  className = "",
  dropdownWidth = "5rem",
  onSearch,
  onOpenChange,
  searchPlaceholder = "Type to search...",
  searchMinChars = 2,
  searchDebounceMs: _searchDebounceMs = 300,
  searchNoResultsMessage = "No results found",
  allowCustomValueOnEmptySearch = false,
  onFooterApply,
  footerPrimaryLabel = "Save",
  hideFooter = true,
  selectionMode = "multiple",
}: MultiSelectWithTagProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceId = useId();
  const singleGroupName = `mswt-single-${instanceId}`;

  const selectedOptions = useMemo(
    () => resolveSelectedOptions(options, value),
    [options, value],
  );

  const setOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
      if (!open) {
        setSearchQuery("");
        setSearchResetKey((k) => k + 1);
      }
    },
    [onOpenChange],
  );

  const runSearch = useCallback(
    async (query: string) => {
      if (!onSearch) return;
      setIsSearching(true);
      try {
        await onSearch(query);
      } finally {
        setIsSearching(false);
      }
    },
    [onSearch],
  );

  const handleSearchChange = useCallback(
    (data: { value?: string }) => {
      const q = data.value ?? "";
      setSearchQuery(q);
      if (!onSearch) return;
      void runSearch(q);
    },
    [onSearch, runSearch],
  );

  const handleToggle = (id: string) => {
    if (selectionMode === "single") {
      onChange(value.includes(id) ? [] : [id]);
      return;
    }
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const handleRemoveTag = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  const handleAddCustomSearchValue = () => {
    const customValue = searchQuery.trim();
    if (!customValue) return;
    if (selectionMode === "single") {
      onChange([customValue]);
    } else if (!value.includes(customValue)) {
      onChange([...value, customValue]);
    }
    setOpen(false);
  };

  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0;

  const handleSelectAll = () => {
    if (selectionMode === "single") return;
    if (allSelected) onChange([]);
    else onChange(options.map((o) => o.id));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpen]);

  const showEmpty =
    isOpen &&
    options.length === 0 &&
    searchQuery.trim().length >= searchMinChars &&
    !isSearching;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`.trim()}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!isOpen);
          }
        }}
        className="flex min-h-[34px] cursor-pointer items-center justify-between gap-2 rounded-e rounded-s border border-primaryGray-15 px-3 py-2 text-sm text-primaryGray-3 hover:border-primaryGray-12"
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {selectedOptions.length === 0 && (
            <span className="text-primaryGray-12">{placeholder}</span>
          )}
          {selectedOptions.map((opt) => (
            <div
              key={opt.id}
              className="flex flex-row gap-1 rounded-e rounded-s border border-primaryGray-1 bg-primaryGray-16 px-2 py-0.5 text-xs font-medium text-primaryGray-1"
            >
              <span>{opt.label}</span>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveTag(opt.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleRemoveTag(opt.id);
                  }
                }}
                className="flex cursor-pointer items-center justify-center rounded hover:bg-primaryGray-14"
                aria-label={`Remove ${opt.label}`}
              >
                <CrossIcon width="12" height="12" fillColor="#000000" />
              </div>
            </div>
          ))}
        </div>
        <ChevronDown
          rotate={isOpen ? 180 : 0}
          stroke="#6b7280"
          width={12}
          height={12}
          className="flex-shrink-0"
        />
      </div>

      {isOpen && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-primaryGray-15 bg-white shadow-lg"
          style={{ minWidth: dropdownWidth }}
        >
          {onSearch ? (
            <div className="border-b border-primaryGray-16 p-2">
              <TextInput
                key={searchResetKey}
                id={`${instanceId}-search`}
                label=""
                hideLabel
                placeholder={searchPlaceholder}
                defaultValue=""
                onChange={handleSearchChange}
              />
              {isSearching && (
                <div className="mt-1 text-tiny text-primaryGray-8">
                  Searching...
                </div>
              )}
            </div>
          ) : null}

          <div className="max-h-60 overflow-auto">
            {selectionMode === "multiple" && options.length > 0 && (
              <label className="flex cursor-pointer items-center gap-2 border-b border-primaryGray-16 px-3 py-2 text-sm font-medium text-primaryGray-3 hover:bg-primaryGray-16">
                <input
                  type="checkbox"
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  checked={allSelected}
                  onChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-gray-300 accent-primaryGray-1"
                  aria-label="Select all"
                />
                <span>Select all</span>
              </label>
            )}
            {options.map((opt) => {
              const isChecked = value.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-primaryGray-3 hover:bg-primaryGray-16"
                >
                  <input
                    type={selectionMode === "single" ? "radio" : "checkbox"}
                    name={
                      selectionMode === "single" ? singleGroupName : undefined
                    }
                    checked={isChecked}
                    onChange={() => handleToggle(opt.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 border-gray-300 accent-primaryGray-1"
                    aria-label={`Select ${opt.label}`}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
            {showEmpty &&
              (allowCustomValueOnEmptySearch && searchQuery.trim() ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-primaryGray-3 hover:bg-primaryGray-16"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddCustomSearchValue();
                  }}
                >
                  Add &quot;{searchQuery.trim()}&quot;
                </button>
              ) : (
                <div className="px-3 py-3 text-sm text-primaryGray-8">
                  {searchNoResultsMessage}
                </div>
              ))}
          </div>

          {!hideFooter && onFooterApply && (
            <div className="flex justify-end border-t border-primaryGray-16 p-2">
              <Button
                buttonType="primary"
                size="small"
                disabled={false}
                onClick={() => onFooterApply(value)}
              >
                {footerPrimaryLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectWithTag;
